import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { isSafeRedirect } from "@/lib/safeRedirect";
import { toast } from "sonner";

export default function SignupPage() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const e = searchParams.get("email");
        const n = searchParams.get("name");
        if (e) setEmail(e);
        if (n) setFullName(n);
    }, [searchParams]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            const u = await signup(email, password, fullName);
            toast.success(`Account created — welcome, ${u.full_name}`);
            // Phase H.8 — only honor `redirect_to` if it matches the central
            // safe-redirect allowlist; otherwise fall through to role-default.
            const redirect = searchParams.get("redirect_to");
            const safe = redirect && isSafeRedirect(redirect) ? redirect : null;
            navigate(safe || "/customer", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const loginHref = (() => {
        const sp = new URLSearchParams();
        const redirect = searchParams.get("redirect_to");
        if (redirect) sp.set("redirect_to", redirect);
        if (email) sp.set("email", email);
        const qs = sp.toString();
        return `/login${qs ? `?${qs}` : ""}`;
    })();

    return (
        <AuthLayout
            eyebrow="Create account"
            title="Start your rental application."
            subtitle="Customers — sign up to submit company info, references, uploads, and signatures in one place."
            footer={
                <>
                    Already have an account?{" "}
                    <Link to={loginHref} data-testid="link-to-login" className="font-medium text-blue-900 hover:underline">
                        Sign in
                    </Link>
                </>
            }
        >
            <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
                <div className="space-y-2">
                    <Label htmlFor="full_name">Full name</Label>
                    <Input
                        id="full_name"
                        data-testid="signup-fullname-input"
                        type="text"
                        autoComplete="name"
                        required
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Jane Doe"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        data-testid="signup-email-input"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                        id="password"
                        data-testid="signup-password-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                    />
                    <p className="text-xs text-slate-500">Use 8+ characters with a mix of letters and numbers.</p>
                </div>

                {error && (
                    <div
                        data-testid="signup-error"
                        className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                    >
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    data-testid="signup-submit-button"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11"
                >
                    {submitting ? "Creating account…" : "Create account"}
                </Button>
            </form>
        </AuthLayout>
    );
}
